import React, { useEffect, useState } from 'react'
import { MenuItemLink, useSidebarState, MenuProps, useResourceDefinitions } from 'react-admin'
import Box from '@mui/material/Box'

import FeaturesIcon from '@mui/icons-material/Settings'
import PricingMultiIcon from '@mui/icons-material/MonetizationOn'
import PricesIcon from '@mui/icons-material/AccountBalance'
import LanguageMinerIcon from '@mui/icons-material/Translate'
import TermsIcon from '@mui/icons-material/TitleRounded'
import AssessmentIcon from '@mui/icons-material/Assessment'
import LabelIcon from '@mui/icons-material/Label'
import SubscriptionsIcon from '@mui/icons-material/CardMembership'

import users from 'user'
import invoices from 'invoice'
import featureSwitches from 'featureSwitch'
import featureParameters from 'featureParameter'
import companies from 'company'
import gatedCommunities from 'gatedCommunity'
import jobs from 'job'
import assets from 'assets'

import SubMenu from './SubMenu'
import UserService from 'UserService'
import { getTableauToken } from 'bi/BiApi'

type MenuName = 'menuPricing' | 'menuFeatures' | 'menuLanguageMiner'

type AccessibleMenuItemsProps = {
  dense: boolean
  state: Record<MenuName, boolean>
  handleToggle: (menu: MenuName) => void
  isAssetManager: boolean
  isBIEnabled: boolean
}

const AccessibleMenuItems = ({
  dense,
  state,
  handleToggle,
  isAssetManager,
  isBIEnabled
}: AccessibleMenuItemsProps) => {
  const isAnyManager = UserService.isAnyManager()
  const showProjects = isAnyManager || UserService.isGcProjectManagerOnly()
  const showPricing = UserService.hasRoles(["ROLE_PLATFORM_MANAGER", "ROLE_SUPPORT"])

  return (
    <>
      {isAnyManager && <MenuItemLink
        to="/users"
        state={{ _scrollToTop: true }}
        primaryText="Users"
        leftIcon={<users.icon />}
        dense={dense}
      />
      }

      {UserService.hasRoles(["ROLE_PLATFORM_MANAGER", "ROLE_SUPPORT", "ROLE_COMMUNITY_MANAGER"]) && (
        <MenuItemLink
          to="/invoices"
          state={{ _scrollToTop: true }}
          primaryText="Invoices"
          leftIcon={<invoices.icon />}
          dense={dense}
        />
      )}

      {showProjects && <MenuItemLink
        to="/jobs"
        state={{ _scrollToTop: true }}
        primaryText={"Projects"}
        leftIcon={<jobs.icon />}
        dense={dense}
      />}

      {UserService.hasRoles([
        "ROLE_PLATFORM_MANAGER",
        "ROLE_SUPPORT",
        "ROLE_COMMUNITY_MANAGER",
        "ROLE_GATED_COMMUNITY_MANAGER"
      ]) && (
        <MenuItemLink
          to="/companies"
          state={{ _scrollToTop: true }}
          primaryText="Companies"
          leftIcon={<companies.icon />}
          dense={dense}
        />
      )}

      {isAnyManager && <MenuItemLink
        to="/gatedCommunities"
        state={{ _scrollToTop: true }}
        primaryText="Gated Comm's"
        leftIcon={<gatedCommunities.icon />}
        dense={dense}
      />}

      {showPricing && (
        <SubMenu
          handleToggle={() => handleToggle("menuPricing")}
          isOpen={state.menuPricing}
          name="Pricing"
          icon={<PricesIcon />}
          dense={dense}
        >
          {showPricing && (
            <MenuItemLink
              to="/pricingrules/dynamic"
              state={{ _scrollToTop: true }}
              primaryText="Dynamic"
              leftIcon={<PricingMultiIcon />}
              dense={dense}
            />
          )}
          {UserService.hasRoles(["ROLE_SUPPORT"]) && (
            <MenuItemLink
              to="/pricingrules/fixed"
              state={{ _scrollToTop: true }}
              primaryText="Fixed"
              leftIcon={<PricingMultiIcon />}
              dense={dense}
            />
          )}
        </SubMenu>
      )}
      {showPricing && (
        <MenuItemLink
          to="/subscriptions"
          state={{ _scrollToTop: true }}
          primaryText="Subscriptions"
          leftIcon={<SubscriptionsIcon />}
          dense={dense}
        />
      )}
      {UserService.hasRoles([]) && (
        <SubMenu
          handleToggle={() => handleToggle("menuFeatures")}
          isOpen={state.menuFeatures}
          name="Features"
          icon={<FeaturesIcon />}
          dense={dense}
        >
          <MenuItemLink
            to="/features/parameters"
            state={{ _scrollToTop: true }}
            primaryText="Parameters"
            leftIcon={<featureParameters.icon />}
            dense={dense}
          />
          <MenuItemLink
            to="/features/switches"
            state={{ _scrollToTop: true }}
            primaryText="Switches"
            leftIcon={<featureSwitches.icon />}
            dense={dense}
          />
        </SubMenu>
      )}
      {isAssetManager && UserService.hasRoles(["ROLE_LANGUAGE_MINER"]) && (
        <SubMenu
          handleToggle={() => handleToggle("menuLanguageMiner")}
          isOpen={state.menuLanguageMiner}
          name="dict.role.ROLE_LANGUAGE_MINER"
          icon={<LanguageMinerIcon />}
          dense={dense}
        >
          <MenuItemLink
            to="/assets"
            state={{ _scrollToTop: true }}
            primaryText="Assets"
            leftIcon={<assets.icon />}
            dense={dense}
          />
          <MenuItemLink
            to="/subassets"
            state={{ _scrollToTop: true }}
            primaryText="Terms"
            leftIcon={<TermsIcon />}
            dense={dense}
          />
        </SubMenu>
      )}
      {isBIEnabled && UserService.hasRoles(["ROLE_BI"]) && (
        <MenuItemLink to="/bi" primaryText="BI" leftIcon={<AssessmentIcon />} />
      )}
      {isAnyManager && <MenuItemLink to="/tags" primaryText="Tags" leftIcon={<LabelIcon />} />}
    </>
  )
}

const Menu = ({ dense = false }: MenuProps) => {
  const [state, setState] = useState({
    menuPricing: false,
    menuFeatures: false,
    menuLanguageMiner: false
  })
  const [open] = useSidebarState()
  const [isAssetManager, setIsAssetManager] = useState<boolean>(false)
  const [isBIEnabled, setBIEnabled] = useState<boolean>(false)
  const resourcesDefinitions = useResourceDefinitions()

  const checkIfBIEnabled = async () => {
    try {
      await getTableauToken()
      setBIEnabled(true)
    } catch (error) {
      console.log('Error fetching Tableau token, disabling BI menu:', error)
    }
  }

  useEffect(() => {
    setIsAssetManager(Object.keys(resourcesDefinitions).includes("assets"))
    checkIfBIEnabled()
  }, [resourcesDefinitions])

  const handleToggle = (menu: MenuName) => {
    setState(state => ({ ...state, [menu]: !state[menu] }))
  }

  return (
    <Box
      sx={{
        width: open ? 200 : 50,
        marginTop: 1,
        marginBottom: 1,
        transition: (theme) =>
          theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen
          })
      }}
    >
      {UserService.canAccess() && (
        <AccessibleMenuItems
          dense={dense}
          state={state}
          handleToggle={handleToggle}
          isAssetManager={isAssetManager}
          isBIEnabled={isBIEnabled}
        />
      )}
    </Box>
  )
}

export default Menu